/**
 * HomePage.tsx — sentry.nself.org home page
 *
 * Purpose:    Placeholder landing page for ɳSentry — pre-launch "coming soon"
 *             communicating the product, pricing, and ɳSelf+ free inclusion
 * Inputs:     none
 * Outputs:    Hero + free-for-nself+ banner + value props + feature grid
 *             + pricing + email notify CTA
 * Constraints: Client-side render; no backend calls that fail pre-launch;
 *              email capture is a mailto stub (no API wiring)
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'
import { FeatureTeaserGrid } from '../components/FeatureTeaserGrid'

export function HomePage() {
  return (
    <>
      {/* ɳSelf+ free-inclusion banner — most prominent message on page */}
      <div
        className="border-b border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/70 px-4 py-3 text-center text-sm"
        role="banner"
        aria-label="ɳSelf+ membership benefit"
      >
        <span className="font-semibold text-sky-700 dark:text-sky-300">ɳSelf+ members get ɳSentry free.</span>{' '}
        <span className="text-sky-200/80">
          Included in ɳSelf+ ($3.99/mo · $39.99/yr) — no add-on needed.
        </span>{' '}
        <a
          href="https://nself.org/pricing"
          className="ml-1 underline underline-offset-2 text-brand-ink hover:text-sky-200 transition-colors"
        >
          Learn about ɳSelf+ →
        </a>
      </div>

      {/* Hero */}
      <section
        className="relative bg-gradient-to-b from-brand-bg via-brand-bg to-brand-surface/50 px-4 py-32 sm:px-6 lg:px-8"
        aria-labelledby="hero-heading"
      >
        <div className="mx-auto max-w-5xl text-center">
          {/* Coming soon pill */}
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-200 dark:border-sky-800/50 bg-sky-50 dark:bg-sky-950/50 px-3 py-1 text-sm text-sky-700 dark:text-sky-300"
            aria-label="Product status: coming soon"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-sky-500" />
            </span>
            Coming Soon · ɳSentry
          </div>

          <h1
            id="hero-heading"
            className="mb-6 text-5xl font-bold tracking-tight text-brand-ink sm:text-6xl lg:text-7xl"
          >
            Privacy-first observability
            <br className="hidden sm:block" />
            <span className="text-sky-700 dark:text-sky-400"> on your terms</span>
          </h1>

          {/* UptimeRobot comparison hook */}
          <p className="mx-auto mb-4 max-w-2xl text-xl font-medium text-gray-200">
            UptimeRobot — but on your own servers.
          </p>

          <p className="mx-auto mb-4 max-w-2xl text-lg text-brand-secondary">
            ɳSentry runs entirely on your infrastructure. Uptime monitoring, status pages,
            incident management, alert routing, SLO tracking, synthetic checks, real-user
            monitoring, error tracking — all self-hosted. No vendor lock-in. No data
            leaves your server.
          </p>

          {/* Pricing summary under hero */}
          <div className="mx-auto mb-10 max-w-2xl rounded-xl border border-sky-200 dark:border-sky-800/40 bg-sky-50 dark:bg-sky-950/30 px-6 py-4">
            <p className="text-sm text-sky-200">
              <span className="font-semibold text-brand-ink">ɳSentry Bundle</span> — $0.99/mo or $9.99/yr.{' '}
              <span className="font-semibold text-sky-700 dark:text-sky-300">
                FREE and included for ɳSelf+ members
              </span>{' '}
              ($3.99/mo · $39.99/yr).
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-center">
            <a
              href="#notify"
              className="rounded-lg bg-sky-500 px-8 py-4 font-semibold text-white transition hover:bg-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              Notify me at launch
            </a>
            <a
              href="https://nself.org/products/nsentry"
              className="rounded-lg border border-brand-card px-8 py-4 font-semibold text-brand-secondary transition hover:border-sky-500 hover:text-sky-700 dark:text-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-700 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              Learn more
            </a>
            <Link
              to="/pricing"
              className="rounded-lg border border-gray-600 px-8 py-4 font-semibold text-brand-secondary transition hover:border-gray-400 hover:text-brand-ink focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              View pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section
        className="border-t border-brand-subtle bg-brand-bg px-4 py-20 sm:px-6 lg:px-8"
        aria-labelledby="value-props-heading"
      >
        <div className="mx-auto max-w-6xl">
          <h2
            id="value-props-heading"
            className="mb-12 text-center text-2xl font-bold text-brand-ink"
          >
            Why ɳSentry?
          </h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <article className="rounded-xl border border-brand-subtle bg-brand-surface/50 p-8 transition hover:border-sky-200 dark:border-sky-700/50 hover:bg-brand-surface">
              <div
                className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-sky-500/20 text-sky-700 dark:text-sky-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-brand-ink">Privacy-first</h3>
              <p className="text-brand-muted">All observability data stays on your infrastructure. No cloud vendor. No data mining. Complete control — forever.</p>
            </article>

            <article className="rounded-xl border border-brand-subtle bg-brand-surface/50 p-8 transition hover:border-sky-200 dark:border-sky-700/50 hover:bg-brand-surface">
              <div
                className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-sky-500/20 text-sky-700 dark:text-sky-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-brand-ink">Flat pricing</h3>
              <p className="text-brand-muted">No per-metric charges. No overage fees. One bundle ($0.99/mo) — or free with ɳSelf+. Scale all you want.</p>
            </article>

            <article className="rounded-xl border border-brand-subtle bg-brand-surface/50 p-8 transition hover:border-sky-200 dark:border-sky-700/50 hover:bg-brand-surface">
              <div
                className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-sky-500/20 text-sky-700 dark:text-sky-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-bold text-brand-ink">Self-hosted</h3>
              <p className="text-brand-muted">Runs on your nSelf server alongside all your other apps. One deploy. One backend. Full cross-platform parity.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Feature grid — 7 plugins */}
      <FeatureTeaserGrid />

      {/* Comparison Teaser */}
      <section
        className="border-t border-brand-subtle bg-brand-surface/30 px-4 py-20 sm:px-6 lg:px-8"
        aria-labelledby="compare-heading"
      >
        <div className="mx-auto max-w-3xl text-center">
          <h2 id="compare-heading" className="mb-4 text-3xl font-bold text-brand-ink">
            How ɳSentry compares
          </h2>
          <p className="mb-2 text-brand-secondary">
            UptimeRobot, Datadog, New Relic, Sentry.io — solid tools.
          </p>
          <p className="mb-8 text-brand-muted">
            ɳSentry does the same job on your own server. Your data. Your rules. No monthly seat charges as you grow.
          </p>
          <Link
            to="/pricing"
            className="inline-block rounded-lg border border-sky-200 dark:border-sky-700 px-8 py-3 font-semibold text-sky-700 dark:text-sky-300 transition hover:border-sky-500 hover:text-brand-ink focus:outline-none focus:ring-2 focus:ring-sky-700 focus:ring-offset-2 focus:ring-offset-gray-950"
          >
            View full comparison →
          </Link>
        </div>
      </section>

      {/* ɳSelf+ free inclusion — primary upsell */}
      <section
        className="border-t border-brand-subtle bg-sky-50 dark:bg-sky-950/30 px-4 py-20 sm:px-6 lg:px-8"
        aria-labelledby="nself-plus-heading"
      >
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-sky-200 dark:border-sky-700/60 bg-sky-900/40 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-sky-700 dark:text-sky-300">
            Best value
          </div>
          <h2 id="nself-plus-heading" className="mb-3 text-3xl font-bold text-brand-ink">
            ɳSentry is <span className="text-sky-700 dark:text-sky-400">free</span> with ɳSelf+
          </h2>
          <p className="mb-2 text-lg font-medium text-brand-ink/90">
            ɳSelf+ includes every bundle — ɳSentry, ɳClaw, ɳChat, ɳTV, ɳFamily, ClawDE — plus all apps and priority support.
          </p>
          <p className="mb-10 text-brand-muted">
            One subscription. No add-ons. No per-bundle charges.
          </p>

          <div className="flex flex-col gap-6 sm:flex-row sm:items-stretch sm:justify-center">
            {/* ɳSentry bundle standalone */}
            <div className="flex-1 rounded-xl border border-brand-card bg-brand-surface/50 px-8 py-7 text-center">
              <p className="mb-1 text-sm font-medium text-brand-muted">ɳSentry Bundle</p>
              <p className="mb-1 text-4xl font-bold text-brand-ink">$0.99</p>
              <p className="mb-3 text-sm text-brand-muted">per month · or $9.99/yr</p>
              <p className="text-xs text-brand-muted">ɳSentry only</p>
            </div>

            {/* ɳSelf+ — highlighted as better deal */}
            <div className="flex-1 rounded-xl border-2 border-sky-600 bg-sky-50 dark:bg-sky-950/50 px-8 py-7 text-center ring-1 ring-sky-500/20">
              <div className="mb-2 inline-block rounded-full bg-sky-500 px-2.5 py-0.5 text-xs font-bold text-white">
                INCLUDES ɳSENTRY FREE
              </div>
              <p className="mb-1 text-sm font-medium text-sky-700 dark:text-sky-300">ɳSelf+</p>
              <p className="mb-1 text-4xl font-bold text-brand-ink">$3.99</p>
              <p className="mb-3 text-sm text-brand-muted">per month · or $39.99/yr</p>
              <p className="text-xs text-sky-700 dark:text-sky-400/80">All 6 bundles + all apps</p>
            </div>
          </div>

          <div className="mt-8">
            <a
              href="https://nself.org/pricing"
              className="rounded-lg bg-sky-500 px-8 py-3 font-semibold text-white transition hover:bg-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              Get ɳSelf+ →
            </a>
          </div>
        </div>
      </section>

      {/* Email capture — launch notification (mailto stub, no API required) */}
      <section
        id="notify"
        className="border-t border-brand-subtle bg-brand-bg px-4 py-20 sm:px-6 lg:px-8"
        aria-labelledby="notify-heading"
      >
        <div className="mx-auto max-w-xl text-center">
          <h2 id="notify-heading" className="mb-3 text-3xl font-bold text-brand-ink">
            Get notified at launch
          </h2>
          <p className="mb-8 text-brand-muted">
            ɳSentry is pre-launch. Drop your email and we'll send one message when it ships — no spam.
          </p>

          {/* Static mailto stub — no backend needed pre-launch */}
          <form
            action="mailto:hello@nself.org"
            method="get"
            className="flex flex-col gap-3 sm:flex-row"
            aria-label="Launch notification form"
          >
            <label htmlFor="notify-email" className="sr-only">
              Email address
            </label>
            <input
              id="notify-email"
              type="email"
              name="subject"
              placeholder="you@example.com"
              required
              autoComplete="email"
              className="flex-1 rounded-lg border border-brand-card bg-brand-surface px-4 py-3 text-brand-ink placeholder:text-brand-muted focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            />
            <button
              type="submit"
              className="rounded-lg bg-sky-500 px-6 py-3 font-semibold text-white transition hover:bg-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-gray-950"
            >
              Notify me
            </button>
          </form>

          <p className="mt-4 text-xs text-brand-muted">
            Or browse{' '}
            <a
              href="https://nself.org/products/nsentry"
              className="text-brand-muted underline underline-offset-2 hover:text-brand-secondary transition-colors"
            >
              nself.org/products/nsentry
            </a>{' '}
            for the full product overview.
          </p>
        </div>
      </section>

      {/* Ecosystem cross-link */}
      <section className="border-t border-brand-subtle bg-brand-bg/60 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-2 text-sm text-brand-muted">Already live from the ɳSelf ecosystem</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-6">
            <a
              href="https://task.nself.org"
              className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:text-emerald-700 dark:text-emerald-300 transition-colors font-medium text-sm"
            >
              ɳTask — free hosted task management →
            </a>
            <span className="hidden text-gray-700 sm:inline" aria-hidden="true">·</span>
            <a
              href="https://chat.nself.org"
              className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300 transition-colors font-medium text-sm"
            >
              ɳChat — self-hosted chat platform →
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
